import { cn } from '@fshr/shared-react';
import Button from '../../button';
import Link from '../../link';
import type { ProcessedCell } from '../types';

interface TableCellProps {
	cell: ProcessedCell;
	columnIndex: number;
}

export default function TableCell({ cell, columnIndex }: TableCellProps) {
	const hasAction = Boolean(cell.ctaLink || cell.onClick);
	const isButtonCta = hasAction && cell.ctaType === 'button';
	const isTextCta = hasAction && cell.ctaType !== 'button';
	const isCol0 = columnIndex === 0;

	const col0HoverUnderline =
		'group-hover/cell:underline decoration-solid underline-offset-[20%] decoration-from-font decoration-skip-ink-auto';

	const textClassName = cn(
		'min-w-0 truncate',
		isCol0
			? cn('text-subtitle2 font-medium', col0HoverUnderline)
			: cn('text-caption', isTextCta && 'cursor-pointer group-hover/cell:underline')
	);

	const innerContent = (
		<>
			{cell.icon ? <span className="shrink-0">{cell.icon}</span> : null}
			<span className={cn(textClassName, isTextCta && isCol0 && 'cursor-pointer')}>
				<span className="block min-w-0 truncate text-text-primary">{cell.text}</span>
			</span>
		</>
	);

	if (isButtonCta) {
		if (cell.ctaLink) {
			return (
				<div className="flex w-full items-center justify-center spacing-card-padding-x-xs">
					<Link
						href={cell.ctaLink}
						intent="ghost"
						size="small"
						onClick={cell.onClick}
						data-sortable-cta
						className="max-w-full"
						{...(cell.isDownload ? { download: true } : {})}
					>
						{cell.icon ? <span className="shrink-0">{cell.icon}</span> : null}
						{cell.text}
					</Link>
				</div>
			);
		}

		return (
			<div className="flex w-full items-center justify-center spacing-card-padding-x-xs">
				<Button
					intent="ghost"
					size="small"
					onClick={cell.onClick}
					data-sortable-cta
					className="max-w-full"
				>
					{cell.icon ? <span className="shrink-0">{cell.icon}</span> : null}
					{cell.text}
				</Button>
			</div>
		);
	}

	if (isTextCta) {
		const sharedProps = {
			className: cn(
				'flex w-full min-w-0 items-center gap-[10px] spacing-card-padding-x-xs',
				'cursor-pointer'
			),
			onClick: cell.onClick,
		};

		if (cell.ctaLink) {
			return (
				<Link
					href={cell.ctaLink}
					intent="unstyled"
					data-sortable-cta
					{...(cell.isDownload ? { download: true } : {})}
					{...sharedProps}
				>
					{innerContent}
				</Link>
			);
		}

		return (
			<button type="button" {...sharedProps}>
				{innerContent}
			</button>
		);
	}

	return (
		<div className="flex min-w-0 items-center gap-[10px] spacing-card-padding-x-xs">
			{innerContent}
		</div>
	);
}
